import React, { useState } from 'react';
import { ChevronDown, Clock } from 'lucide-react';
import { Label, Prose, BulletList, Pills } from './SectionShell';

const cap = t => t.charAt(0).toUpperCase() + t.slice(1);
export const splitPrice = (raw = '') => {
  let text = String(raw).trim();
  const notes = [];
  text = text.replace(/\(([^)]+)\)/g, (_, n) => { notes.push(n.trim()); return ''; }).trim();
  const parts = text.split(/;|\s\|\s|\n/).flatMap(p => p.split(/,\s+(?=[a-zA-Z$])/)).map(p => p.trim()).filter(Boolean);
  return { headline: parts[0] || '', details: [...parts.slice(1), ...notes].map(cap) };
};

const PriceBlock = ({ price, d, size = 'lg' }) => {
  const { headline, details } = splitPrice(price);
  const big = size === 'lg' && headline.length <= 16;
  return (
    <div className="mt-2">
      <p className={`font-semibold tabular-nums tracking-tight text-teal-600 ${d ? 'text-teal-300' : ''} ${big ? 'text-[26px] leading-none' : 'text-[15px] leading-snug'}`}>{headline}</p>
      {details.length > 0 && (
        <ul className="mt-3 space-y-1.5">
          {details.map((x, i) => <li key={i} className={`text-[12px] flex gap-2 leading-snug ${d ? 'text-gray-300' : 'text-gray-600'}`}><span className="mt-[6px] w-1 h-1 rounded-full bg-teal-400 shrink-0" />{x}</li>)}
        </ul>
      )}
    </div>
  );
};

const muted = d => (d ? 'text-gray-400' : 'text-gray-500');
const strong = d => (d ? 'text-white' : 'text-gray-900');
const tile = d => `rounded-xl border p-5 ${d ? 'bg-white/[0.02] border-white/[0.07]' : 'bg-gray-50/70 border-gray-100'}`;

export const OffersRead = ({ offerings, tiered, isDarkMode: d }) => {
  const priced = tiered ? offerings.filter(o => o.price) : [];
  const rest = tiered && priced.length >= 2 ? offerings.filter(o => !o.price) : offerings;
  return (
    <div className="space-y-6">
      {priced.length >= 2 && (
        <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="bp-offer-tiers">
          {priced.map((o, i) => (
            <div key={i} className={`${tile(d)} flex flex-col`}>
              <p className={`text-[13px] font-semibold ${strong(d)}`}>{o.name}</p>
              <PriceBlock price={o.price} d={d} />
              <p className={`text-[12.5px] leading-relaxed mt-4 pt-4 border-t ${d ? 'border-white/[0.06]' : 'border-gray-200/70'} ${muted(d)}`}>{o.description}</p>
            </div>
          ))}
        </div>
      )}
      {rest.length > 0 && (
        <div className="grid md:grid-cols-2 gap-x-8 gap-y-5" data-testid="bp-offer-list">
          {rest.map((o, i) => (
            <div key={i} className={`pb-5 border-b ${d ? 'border-white/[0.06]' : 'border-gray-100'}`}>
              <div className="flex items-start gap-3">
                <p className={`text-[14px] font-semibold flex-1 ${strong(d)}`}>{o.name}</p>
                {o.price && <span className={`text-[11.5px] font-semibold px-2.5 py-0.5 rounded-full ${d ? 'bg-amber-400/10 text-amber-300' : 'bg-amber-50 text-amber-700'} shrink-0 max-w-[50%] text-right`}>{splitPrice(o.price).headline}</span>}
              </div>
              {o.description && <p className={`text-[13px] leading-relaxed mt-1.5 ${muted(d)}`}>{o.description}</p>}
              {o.price && splitPrice(o.price).details.length > 0 && <p className={`text-[12px] mt-1.5 ${muted(d)}`}>{splitPrice(o.price).details.join(' · ')}</p>}
              {o.duration && <p className={`text-[11.5px] mt-2 inline-flex items-center gap-1 ${muted(d)}`}><Clock size={11} />{o.duration}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const FaqsRead = ({ faqs, isDarkMode: d }) => {
  const [open, setOpen] = useState(null);
  return (
    <div className="grid lg:grid-cols-2 gap-x-8" data-testid="bp-faq-list">
      {faqs.map((f, i) => (
        <div key={i} className={`border-b ${d ? 'border-white/[0.06]' : 'border-gray-100'}`}>
          <button type="button" onClick={() => setOpen(open === i ? null : i)} className="w-full flex items-start gap-3 py-3.5 text-left" data-testid={`bp-faq-${i}`}>
            <span className={`text-[13.5px] font-medium flex-1 ${strong(d)}`}>{f.q}</span>
            {f.source === 'inferred' && <span className={`text-[10px] px-2 py-0.5 rounded-full ${d ? 'bg-white/5 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>inferred</span>}
            <ChevronDown size={14} className={`mt-0.5 shrink-0 transition-transform ${muted(d)} ${open === i ? 'rotate-180' : ''}`} />
          </button>
          {open === i && <p className={`text-[13px] leading-relaxed pb-4 pr-6 ${muted(d)}`}>{f.a || 'No answer yet.'}</p>}
        </div>
      ))}
    </div>
  );
};

export const AudienceRead = ({ customers: c, isDarkMode: d }) => (
  <div className="space-y-7">
    {c.target_audience && (
      <div className={`${tile(d)} border-l-4 !border-l-indigo-400`} data-testid="bp-icp">
        <Label isDarkMode={d}>Ideal customer</Label>
        <Prose isDarkMode={d}>{c.target_audience}</Prose>
      </div>
    )}
    <div className="grid md:grid-cols-2 gap-8">
      {(c.pain_points || []).length > 0 && <div><Label isDarkMode={d}>Pain points</Label><BulletList items={c.pain_points} isDarkMode={d} tone="indigo" /></div>}
      {(c.objections || []).length > 0 && <div><Label isDarkMode={d}>Objections to handle</Label><BulletList items={c.objections} isDarkMode={d} tone="amber" /></div>}
    </div>
  </div>
);

const AXES = [['formality', 'Casual', 'Formal'], ['warmth', 'Neutral', 'Warm'], ['energy', 'Calm', 'Energetic'], ['technicality', 'Simple', 'Technical']];

export const VoiceRead = ({ tone: t, isDarkMode: d }) => (
  <div className="grid lg:grid-cols-[1fr_1.2fr] gap-8">
    <div className="space-y-4">
      {AXES.map(([k, lo, hi]) => (
        <div key={k}>
          <div className={`flex justify-between text-[11px] mb-1.5 ${muted(d)}`}><span>{lo}</span><span>{hi}</span></div>
          <div className={`h-1.5 rounded-full relative ${d ? 'bg-white/10' : 'bg-gray-100'}`}>
            <span className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-cyan-400 shadow" style={{ left: `calc(${(t[k] ?? 5) * 10}% - 6px)` }} />
          </div>
        </div>
      ))}
      {(t.personality_adjectives || []).length > 0 && <div className="pt-2"><Pills items={t.personality_adjectives} tone="cyan" isDarkMode={d} /></div>}
    </div>
    {(t.example_phrases || []).length > 0 && (
      <div className="grid sm:grid-cols-2 gap-3">
        {t.example_phrases.map((p, i) => <blockquote key={i} className={`${tile(d)} text-[13.5px] italic leading-snug ${strong(d)}`}>“{p}”</blockquote>)}
      </div>
    )}
  </div>
);

export const ProofRead = ({ proof: p, gbp, isDarkMode: d }) => (
  <div className="grid md:grid-cols-2 gap-8">
    {[['guarantees', 'Guarantees'], ['certifications', 'Certifications'], ['awards', 'Awards'], ['review_themes', 'What customers praise']]
      .filter(([k]) => (p[k] || []).length).map(([k, label]) => (
        <div key={k}><Label isDarkMode={d}>{label}</Label><BulletList items={p[k]} isDarkMode={d} tone="emerald" /></div>
      ))}
    {gbp?.reviews?.length > 0 && (
      <div className="md:col-span-2 grid md:grid-cols-3 gap-4">
        {gbp.reviews.slice(0, 3).map((r, i) => (
          <div key={i} className={tile(d)}>
            <p className={`text-[12px] font-semibold ${strong(d)}`}>{r.author} <span className="text-amber-500">{'★'.repeat(Math.round(r.rating || 0))}</span></p>
            <p className={`text-[12.5px] leading-relaxed mt-2 line-clamp-5 ${muted(d)}`}>{r.text}</p>
          </div>
        ))}
      </div>
    )}
  </div>
);

export const GuardrailsRead = ({ doNotSay = [], competitors = [], isDarkMode: d }) => (
  <div className="grid md:grid-cols-2 gap-8">
    <div><Label isDarkMode={d}>Never say</Label>{doNotSay.length ? <BulletList items={doNotSay} isDarkMode={d} tone="rose" /> : <p className={`text-[13px] ${muted(d)}`}>None set.</p>}</div>
    <div><Label isDarkMode={d}>Competitors to handle carefully</Label>{competitors.length ? <Pills items={competitors} tone="fuchsia" isDarkMode={d} /> : <p className={`text-[13px] ${muted(d)}`}>None set.</p>}</div>
  </div>
);

export const LookRead = ({ colors = [], logo, isDarkMode: d }) => (
  <div className="flex items-center gap-6 flex-wrap">
    {logo && <img src={logo} alt="Brand visual" className="h-20 max-w-[180px] rounded-xl object-cover border border-black/5" data-testid="brand-logo-preview" />}
    {colors.map(c => (
      <div key={c} className="text-center">
        <div className="w-14 h-14 rounded-2xl border border-black/5 shadow-sm" style={{ background: c }} />
        <p className={`text-[10.5px] mt-1.5 font-mono ${muted(d)}`}>{c}</p>
      </div>
    ))}
  </div>
);
